import React, {useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';

const SECTIONS = [
  {
    id: 'scoring',
    title: '計分規則',
    emoji: '🎯',
    items: [
      ['標準 21 分模式', '採用每球得分制，先到 21 分並領先兩分者勝出；29 比 29 後先到 30 分者勝出。'],
      ['賽制', '常見賽制為三局兩勝。正式賽事仍應以主辦單位及當時適用的規則為準。'],
      ['自訂模式', '11、15 和以 30 分為目標的選項是本工具提供的自訂或練習模式，不代表全部符合正式 BWF 賽制。']
    ]
  },
  {
    id: 'serving',
    title: '發球提示',
    emoji: '🏸',
    items: [
      ['發球區判斷', '發球方分數為偶數時提示右發球區，奇數時提示左發球區。'],
      ['接球位置', '接球員位於發球員的對角發球區。'],
      ['雙打限制', '場地圖只提供發球區提示，不等於完整的雙打站位、輪轉或發球次序判定。']
    ]
  },
  {
    id: 'change',
    title: '換邊規則',
    emoji: '↔️',
    items: [
      ['局間換邊', '每局結束後，按「下一局」時系統會交換畫面兩方的場區。'],
      ['下一局發球', '上一局勝方在下一局先發球，並從右發球區開始。'],
      ['標準 21 分決勝局', '任一方先到 11 分時，系統交換場區並顯示提示。'],
      ['自訂模式', '決勝局在指定分數一半向上取整時交換場區；這是工具的產品規則，不應視為正式 BWF 規則。']
    ]
  },
  {
    id: 'alerts',
    title: '局末分／賽末分',
    emoji: '⚠️',
    items: [
      ['局末分', '當一方再得一分即可贏得該局時，畫面顯示局末分。'],
      ['賽末分', '當一方再得一分即可贏得整場比賽時，畫面顯示賽末分。'],
      ['提示用途', '提示只協助計分，不取代正式賽事的裁判判定。']
    ]
  }
];

const BadmintonRulesModal = ({open, onClose}) => {
  const [active, setActive] = useState('scoring');
  const section = SECTIONS.find(item => item.id === active);

  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <motion.button
            type="button"
            aria-label="關閉羽毛球規則"
            className="absolute inset-0 bg-slate-950/90 backdrop-blur-md"
            onClick={onClose}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
          />
          <motion.article
            role="dialog"
            aria-modal="true"
            aria-labelledby="rules-title"
            className="relative z-10 flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0a1220] shadow-2xl"
            initial={{opacity: 0, scale: 0.95, y: 20}}
            animate={{opacity: 1, scale: 1, y: 0}}
            exit={{opacity: 0, scale: 0.95, y: 20}}
          >
            <header className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <div>
                <h2 id="rules-title" className="text-xl font-black text-white">
                  羽毛球規則說明
                </h2>
                <p className="mt-1 text-xs text-white/50">
                  正式賽事請以主辦單位適用規則為準
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="關閉羽毛球規則"
                className="rounded-xl bg-white/5 p-2.5 text-xl text-white/60 hover:bg-white/10 hover:text-white"
              >
                ×
              </button>
            </header>

            <nav className="flex shrink-0 gap-2 overflow-x-auto border-b border-white/5 px-4 py-3">
              {SECTIONS.map(item => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => setActive(item.id)}
                  className={`shrink-0 rounded-xl border px-4 py-2.5 text-sm font-black ${
                    active === item.id
                      ? 'border-indigo-500/40 bg-indigo-500/25 text-indigo-300'
                      : 'border-white/10 bg-white/[0.04] text-white/50'
                  }`}
                >
                  {item.emoji} {item.title}
                </button>
              ))}
            </nav>

            <section className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
              <h3 className="text-lg font-black text-white">
                {section.emoji} {section.title}
              </h3>
              {section.items.map(([title, description]) => (
                <div
                  key={title}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                >
                  <h4 className="text-sm font-black text-white">{title}</h4>
                  <p className="mt-2 text-sm leading-6 text-white/65">
                    {description}
                  </p>
                </div>
              ))}
            </section>
          </motion.article>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BadmintonRulesModal;